import { useAppDispatch } from "hooks/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectState, setAccountReceivables,
} from "../accountReceivable.slice";
import { selectState as mainSlice } from "../../Main/main.slice";
import classes from "../../../styles/searchBar.module.scss";
import { BiSearchAlt } from "react-icons/bi";
import DatePicker from "react-datepicker";
import { useOnClickOutside } from "hooks/ClickOutside";

export const TableHeader = () => {
  const dispatch = useAppDispatch();

  const [searchActive, setSearchActive] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");
  const { allAccReceivablesWithPayments } = useSelector(selectState);
  const { selectedIndex } = useSelector(mainSlice);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchText("");
    setStartDate(null);
    setEndDate(null);
    setIsDatePickerVisible(false);
  }, [selectedIndex]);

  const handleEmptyDate = () => {
    setIsDatePickerVisible(true);
  };

  const searchAndFilterList = useMemo(() => {
    const removeDiacritics = (str: string) => {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
    };

    const tempAccountReceivables = [...allAccReceivablesWithPayments];

    return tempAccountReceivables.filter(item => {
      // Search text matching
      const searchMatch = !searchText || (
        removeDiacritics(String(item.project.name)).toLowerCase()
          .includes(removeDiacritics(searchText).toLowerCase()) ||
        removeDiacritics(item.accountReceivable.invoiceId).toLowerCase()
          .includes(removeDiacritics(searchText).toLowerCase()));

      // Date range matching
      const checkDate = (date: Date | string) => {
        const compareDate = new Date(date);
        // Reset hours to midnight for consistent date comparison
        compareDate.setHours(0, 0, 0, 0);

        const normalizedStartDate = startDate
          ? new Date(startDate.setHours(0, 0, 0, 0)) : null;
        const normalizedEndDate = endDate
          ? new Date(endDate.setHours(0, 0, 0, 0)) : null;

        if (normalizedStartDate && normalizedEndDate) {
          return compareDate >= normalizedStartDate && compareDate <= normalizedEndDate;
        }
        if (normalizedStartDate) {
          return compareDate >= normalizedStartDate;
        }
        return true;
      };

      const dateMatch = (!startDate && !endDate) || (
        checkDate(item.accountReceivable.invoiceDate) ||
        item.receivedPayments.some(payment => checkDate(payment.date))
      );

      return searchMatch && dateMatch;
    });
  }, [searchText, startDate, endDate]);

  // Update dispatch when filtered list changes
  useEffect(() => {
    dispatch(setAccountReceivables(searchAndFilterList));
  }, [searchAndFilterList, dispatch]);

  useOnClickOutside(containerRef, () => {
    if (startDate === null && endDate === null) {
      setIsDatePickerVisible(false);
    }
  });

  const searchList = (input: string) => {
    const searchTerm = input.toLowerCase();
    setSearchText(searchTerm);
  };

  const handleDateChange = (update: [Date | null, Date | null]) => {
    const [start, end] = update;
    setStartDate(start);
    setEndDate(end);
  };

  const shouldShowDatePicker = isDatePickerVisible || !!startDate || !!endDate;

  return (
    <div className="g-grid g-header">
      <div className={`row ${!shouldShowDatePicker ? "" : "rowWithDatePicker"}`}>
        <div className="col" />
        {(!searchActive && searchText === "") &&
          <div
            className={`col center ${classes.pointer}`}
            onClick={() => setSearchActive(true)}
          >
            {searchText !== "" ? searchText : "br. fakture"}
          </div>}
        {(!searchActive && searchText === "") &&
          <div className="col separatorLine">
            <div
              className={classes.searchBarSpan}
              onClick={() => setSearchActive(true)}
            >
              <span>{searchText !== "" ? searchText : "za koji posao"}</span>
              <BiSearchAlt size={13} />
            </div>
          </div>}
        {(!searchActive && searchText !== "") &&
          <div
            className={`col ${classes.searchInputCol}`}
            onClick={() => setSearchActive(true)}
          >
            <span>{searchText}</span>
            <BiSearchAlt size={13} />
          </div>}
        {searchActive &&
          <div className={`col ${classes.searchInputCol}`}>
            <input type="text"
              autoFocus
              placeholder="br.fakture / za koji posao"
              onChange={(e) => searchList(e.target.value)}
              onBlur={() => setSearchActive(false)}
              value={searchText} />
          </div>}
        {!shouldShowDatePicker ? (
          <div
            className={`col center separatorLine ${classes.dateCol}`}
            onClick={handleEmptyDate}
          >
            <span>datum</span>
            <BiSearchAlt size={13} />
          </div>
        ) : (
          <div className="col center separatorLine" ref={containerRef}>
            <div className="dateRange">
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                showMonthDropdown
                showYearDropdown
                dateFormat="dd.MM.yy"
                onChange={handleDateChange}
                isClearable={true}
                autoFocus
                inline={false}
              />
            </div>
          </div>
        )}
        <div className="col center separatorLine">valuta</div>
        <div className="col center separatorLine">fakturisano</div>
        <div className="col center separatorLine">dug</div>
        <div className="col center separatorLine">uplaćeno</div>
        <div className="col separatorLine"></div>
        <div className="col vat">pdv</div>
        <div className="col center">plan</div>
      </div>
    </div>
  );
};
